/**
 * Types and constants for the extraction pipeline UI.
 */

export type JobState =
  | 'idle'
  | 'pending'
  | 'downloading'
  | 'transcribing'
  | 'analyzing'
  | 'enriching'
  | 'completed'
  | 'failed'
  | 'not_found';

export type TimingStep =
  'downloading' | 'transcribing' | 'analyzing' | 'enriching';

export interface StepTimings {
  downloading: number | null;
  transcribing: number | null;
  analyzing: number | null;
  enriching: number | null;
  total: number | null;
}

export interface SseEventData {
  state: JobState;
  error?: string;
  errorMessage?: string | null;
  stepTimings?: StepTimings;
  title?: string | null;
  thumbnailUrl?: string | null;
  description?: string | null;
  duration?: number | null;
  language?: string | null;
}

/**
 * Ordered pipeline states for the progress bar.
 * Each entry becomes a `<li class="step">` in the UI.
 */
export const PIPELINE_STEPS: JobState[] = [
  'pending',
  'downloading',
  'transcribing',
  'analyzing',
  'enriching',
  'completed',
];

/** Steps that have client-side duration tracking */
export const TIMING_STEPS: readonly TimingStep[] = [
  'downloading',
  'transcribing',
  'analyzing',
  'enriching',
] as const;

const IN_PROGRESS_STATES = new Set<JobState>([
  'pending',
  'downloading',
  'transcribing',
  'analyzing',
  'enriching',
]);

/** Whether a pipeline state is still actively processing */
export function isInProgressState(s: JobState): boolean {
  return IN_PROGRESS_STATES.has(s);
}

/** Whether a pipeline state is terminal (no further transitions) */
export function isTerminalState(s: JobState): boolean {
  return s === 'completed' || s === 'failed' || s === 'not_found';
}

/**
 * Get the CSS class for a step in the progress bar.
 * A step is "active" if the current state is at or past that step.
 */
export function stepCssClass(currentState: JobState, step: JobState): string {
  if (currentState === 'failed' || currentState === 'not_found') {
    return 'step-error';
  }
  const currentIdx = PIPELINE_STEPS.indexOf(currentState);
  const stepIdx = PIPELINE_STEPS.indexOf(step);
  return currentIdx >= stepIdx ? 'step-primary' : '';
}

// ── Fine-grained pipeline step events (SSE v2: snapshot/step/source) ─────────

/** Mirrors PipelineStepId in @favoris/core (types/pipeline-events.ts) */
export type PipelineStepId =
  | 'fetch-metadata'
  | 'download'
  | 'transcribe'
  | 'describe-image'
  | 'download-photos'
  | 'describe-photos'
  | 'summarize'
  | 'extract-entities'
  | 'enrich'
  | 'persist-mentions'
  | 'extract-recipes'
  | 'fetch-ratings';

export type StepStatus = 'started' | 'completed' | 'failed' | 'skipped';

/** Execution order for the fine-grained timeline */
export const STEP_ORDER: readonly PipelineStepId[] = [
  'fetch-metadata',
  'download',
  'transcribe',
  'describe-image',
  'download-photos',
  'describe-photos',
  'summarize',
  'extract-entities',
  'enrich',
  'persist-mentions',
  'extract-recipes',
  'fetch-ratings',
];

/** Route-specific lead-in steps; a run only ever emits one route's lead-ins. */
const ROUTE_LEAD_INS: readonly (readonly PipelineStepId[])[] = [
  ['fetch-metadata', 'download', 'transcribe'],
  ['describe-image'],
  ['download-photos', 'describe-photos'],
];

/**
 * Narrows STEP_ORDER to the route observed in the given step ids. Lead-in
 * steps of other routes never emit events (not even 'skipped'), so keeping
 * them would render permanent pending rows in the timeline. Defaults to the
 * video route until a lead-in event identifies the route.
 */
export function stepOrderForRoute(
  observed: Iterable<PipelineStepId>
): PipelineStepId[] {
  const seen = new Set(observed);
  const active =
    ROUTE_LEAD_INS.find((leadIns) => leadIns.some((id) => seen.has(id))) ??
    ROUTE_LEAD_INS[0]!;
  const otherRoutes = new Set(
    ROUTE_LEAD_INS.filter((route) => route !== active).flat()
  );
  return STEP_ORDER.filter((id) => !otherRoutes.has(id));
}

/** Partial source metadata delivered mid-extraction (SSE `source` event) */
export interface SourcePatch {
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  duration?: number;
  language?: string;
}

/** One pipeline step event (SSE `step` event; mirrors core PipelineEvent) */
export interface StepEvent {
  runId: string;
  sourceId: string;
  stepId: PipelineStepId;
  status: StepStatus;
  progress: number;
  summary?: Record<string, unknown>;
  error?: string;
  sourcePatch?: SourcePatch;
  state?: JobState;
  timestamp: number;
}

/** Initial state sent on SSE connect (`snapshot` event) */
export interface SnapshotEvent {
  source: SseEventData & { id: string };
  runId: string | null;
  steps: StepEvent[];
}

function formatAudioLength(seconds: unknown): string | null {
  if (typeof seconds !== 'number' || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins > 0 ? `${mins}m${String(secs).padStart(2, '0')}s` : `${secs}s`;
}

function asNumber(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

/**
 * Human-readable one-liner of what a completed step accomplished,
 * built from the step's summary payload.
 */
export function summaryLabel(
  stepId: PipelineStepId,
  summary: Record<string, unknown> | undefined
): string | null {
  if (!summary) return null;
  switch (stepId) {
    case 'fetch-metadata':
      return typeof summary['title'] === 'string' && summary['title']
        ? `Found "${summary['title']}"`
        : null;
    case 'download': {
      if (summary['cached']) return 'Reused cached audio';
      const length = formatAudioLength(summary['audioSeconds']);
      return length ? `Downloaded ${length} of audio` : 'Audio downloaded';
    }
    case 'transcribe': {
      const length = formatAudioLength(summary['audioSeconds']);
      const lang =
        typeof summary['language'] === 'string'
          ? ` (${summary['language']})`
          : '';
      if (summary['cached']) return `Reused transcript${lang}`;
      return length ? `Transcribed ${length}${lang}` : `Transcribed${lang}`;
    }
    case 'describe-image': {
      const length = asNumber(summary['descriptionLength']);
      return length > 0
        ? `Described image (${length} chars)`
        : 'Image described';
    }
    case 'download-photos': {
      const count = asNumber(summary['imageCount']);
      return `Found ${count} image${count === 1 ? '' : 's'}`;
    }
    case 'describe-photos': {
      const count = asNumber(summary['imageCount']);
      return `Described ${count} image${count === 1 ? '' : 's'}`;
    }
    case 'summarize':
      return summary['hasRecipes']
        ? 'Summary ready · recipe detected'
        : 'Summary ready';
    case 'extract-entities': {
      const count = asNumber(summary['mentionCount']);
      const byType = summary['byType'] as Record<string, number> | undefined;
      const detail = byType
        ? Object.entries(byType)
            .map(([type, n]) => `${n} ${type.replace('_', ' ')}`)
            .join(', ')
        : '';
      return detail
        ? `${count} mention${count === 1 ? '' : 's'} (${detail})`
        : `${count} mention${count === 1 ? '' : 's'}`;
    }
    case 'enrich': {
      const matched = asNumber(summary['matched']);
      const total = matched + asNumber(summary['unmatched']);
      const byProvider = summary['byProvider'] as
        Record<string, number> | undefined;
      const providers = byProvider ? Object.keys(byProvider).join(', ') : '';
      if (total === 0) return null;
      return providers
        ? `Matched ${matched}/${total} on ${providers}`
        : `Matched ${matched}/${total}`;
    }
    case 'persist-mentions': {
      const mentions = asNumber(summary['mentions']);
      return `Saved ${mentions} mention${mentions === 1 ? '' : 's'}`;
    }
    case 'extract-recipes': {
      const count = asNumber(summary['recipeCount']);
      return count > 0
        ? `${count} recipe${count === 1 ? '' : 's'} extracted`
        : null;
    }
    case 'fetch-ratings': {
      const rated = asNumber(summary['rated']);
      return rated > 0
        ? `Ratings for ${rated} item${rated === 1 ? '' : 's'}`
        : null;
    }
    default:
      return null;
  }
}
